import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  User,
  Mail,
  Phone,
  Globe,
  School,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Loader2,
  Save,
  Lock,
  UserCircle,
  Calendar,
  FileText,
  BookOpen,
  Edit,
  Shield,
  Award,
  Sparkles,
  ExternalLink,
  Clock,
  AlertCircle,
  Upload,
  Map,
  Landmark,
  Wallet
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import profileService, { UserProfile, SchoolProfile } from '../services/profileService'
import HowToReachForm from '../components/HowToReachForm'
import MapDisplay from '../components/MapDisplay'
import MapLocationPicker from '../components/MapLocationPicker'
import CountrySelect from '../components/CountrySelect'
import toast from 'react-hot-toast'
import { validatePassword } from '../validation'

const Profile: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState<'user' | 'school'>('user')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // User profile state
  const [isEditingUser, setIsEditingUser] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null)
  const [userFormData, setUserFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  })
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  // Verification documents state (each item has url and type for display label)
  const [verificationStatus, setVerificationStatus] = useState<string>('PENDING')
  const [verificationDocuments, setVerificationDocuments] = useState<Array<{ url: string; type: string }>>([])
  const [verificationMeta, setVerificationMeta] = useState<Record<string, string>>({})
  const [loadingVerification, setLoadingVerification] = useState(false)

  // School profile state
  const [isEditingSchool, setIsEditingSchool] = useState(false)
  const [schoolProfile, setSchoolProfile] = useState<SchoolProfile | null>(null)
  type HowToReachOption = { mode: 'TRAIN' | 'BUS' | 'TAXI' | 'AIR'; details: string }
  type HowToReachStation = { name: string; options: HowToReachOption[] }
  const [schoolFormData, setSchoolFormData] = useState<{
    name: string
    slug: string
    description: string
    shortDescription: string
    location: string
    country: string
    city: string
    address: string
    phone: string
    email: string
    website: string
    bankDetails: {
      bankName: string
      accountNumber: string
      accountHolderName: string
      ifscCode: string
      branchName: string
    }
    paypalDetails: {
      paypalEmail: string
    }
    howToReach?: HowToReachStation[]
  }>({
    name: '',
    slug: '',
    description: '',
    shortDescription: '',
    location: '',
    country: '',
    city: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    bankDetails: {
      bankName: '',
      accountNumber: '',
      accountHolderName: '',
      ifscCode: '',
      branchName: '',
    },
    paypalDetails: {
      paypalEmail: '',
    },
    howToReach: [{ name: '', options: [{ mode: 'TRAIN', details: '' }] }],
  })
  const [showSchoolMap, setShowSchoolMap] = useState(false)
  const [schoolCoordinates, setSchoolCoordinates] = useState<{ lat: number; lng: number } | null>(null)
  const [showMapPicker, setShowMapPicker] = useState(false)

  useEffect(() => {
    fetchData()
    fetchVerificationStatus()
  }, [])

  useEffect(() => {
    const state = location.state as { openTab?: 'user' | 'school' } | null
    if (state?.openTab === 'school') {
      setActiveTab('school')
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, location.pathname, navigate])

  const fetchVerificationStatus = async () => {
    try {
      setLoadingVerification(true)
      const response = await profileService.getVerificationStatus()
      if (response.success) {
        setVerificationStatus(response.data.status || 'PENDING')
        const docs = response.data.documents || []
        setVerificationDocuments(Array.isArray(docs) ? docs.map((d: any) => typeof d === 'string' ? { url: d, type: 'additional' } : { url: d.url || d, type: d.type || 'additional' }) : [])
        setVerificationMeta(response.data.verificationMeta || {})
      }
    } catch {
      // Silently fail
    } finally {
      setLoadingVerification(false)
    }
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const [userResponse, schoolResponse] = await Promise.all([
        profileService.getUserProfile(),
        profileService.getSchoolProfile().catch(() => ({ success: false, data: null }))
      ])

      if (userResponse.success) {
        const userData = userResponse.data
        setUserProfile(userData)
        setUserFormData({
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          phone: userData.phone || '',
        })
      }

      if (schoolResponse.success && schoolResponse.data) {
        const schoolData = (schoolResponse.data as any).school || schoolResponse.data
        setSchoolProfile(schoolData as any)
        const stations = schoolData.howToReach?.stations
        setSchoolFormData({
          name: schoolData.name || '',
          slug: schoolData.slug || '',
          description: schoolData.description || '',
          shortDescription: schoolData.shortDescription || '',
          location: schoolData.location || '',
          country: schoolData.country || '',
          city: schoolData.city || '',
          address: schoolData.address || '',
          phone: schoolData.phone || '',
          email: schoolData.email || '',
          website: schoolData.website || '',
          bankDetails: {
            bankName: schoolData.bankDetails?.bankName || '',
            accountNumber: schoolData.bankDetails?.accountNumber || '',
            accountHolderName: schoolData.bankDetails?.accountHolderName || '',
            ifscCode: schoolData.bankDetails?.ifscCode || '',
            branchName: schoolData.bankDetails?.branchName || '',
          },
          paypalDetails: {
            paypalEmail: schoolData.paypalDetails?.paypalEmail || '',
          },
          howToReach: stations?.length
            ? stations.map((s: any) => ({
                name: s.name || '',
                options: (s.options || []).length
                  ? s.options.map((o: any) => ({ mode: o.mode || 'TRAIN', details: o.details || '' }))
                  : [{ mode: 'TRAIN' as const, details: '' }],
              }))
            : [{ name: '', options: [{ mode: 'TRAIN', details: '' }] }],
        })
        setSchoolCoordinates(schoolData.coordinates?.lat != null && schoolData.coordinates?.lng != null
          ? { lat: schoolData.coordinates.lat, lng: schoolData.coordinates.lng }
          : null)
      }
    } catch (err: any) {
      console.error('Error fetching profile data:', err)
      toast.error('Failed to load profile data')
    } finally {
      setLoading(false)
    }
  }

  const handleUserInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setUserFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSchoolInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setSchoolFormData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSchoolBankDetailsChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target
    setSchoolFormData(prev => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [name]: value,
      },
    }))
    setError('')
  }

  const handleSchoolPayPalDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setSchoolFormData(prev => ({
      ...prev,
      paypalDetails: {
        ...prev.paypalDetails,
        [name]: value,
      },
    }))
    setError('')
  }

  const getOptionalTrimmedValue = (value: string): string | undefined => {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({ ...prev, [name]: value }))
    setError('')
  }

  const handleSaveUserProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const response = await profileService.updateUserProfile(userFormData)
      if (response.success) {
        setUserProfile(response.data)
        setIsEditingUser(false)
        setSuccess('User profile updated successfully!')
        const updatedUserResponse = await profileService.getUserProfile()
        if (updatedUserResponse.success) {
          setUserProfile(updatedUserResponse.data)
        }
        toast.success('Profile updated successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update profile'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleSaveSchoolProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const payload: any = {
        name: schoolFormData.name.trim(),
        slug: schoolFormData.slug.trim(),
        description: schoolFormData.description.trim(),
        location: schoolFormData.location.trim() || schoolFormData.city.trim(),
        country: schoolFormData.country.trim(),
        city: schoolFormData.city.trim(),
      }
      const shortDescription = getOptionalTrimmedValue(schoolFormData.shortDescription)
      const address = getOptionalTrimmedValue(schoolFormData.address)
      const phone = getOptionalTrimmedValue(schoolFormData.phone)
      const email = schoolFormData.email.trim() ? schoolFormData.email.trim().toLowerCase() : undefined
      const website = getOptionalTrimmedValue(schoolFormData.website)

      if (shortDescription !== undefined) payload.shortDescription = shortDescription
      if (address !== undefined) payload.address = address
      if (phone !== undefined) payload.phone = phone
      if (email !== undefined) payload.email = email
      if (website !== undefined) payload.website = website

      const normalizedBankDetails = {
        bankName: schoolFormData.bankDetails.bankName.trim(),
        accountNumber: schoolFormData.bankDetails.accountNumber.trim(),
        accountHolderName: schoolFormData.bankDetails.accountHolderName.trim(),
        ifscCode: schoolFormData.bankDetails.ifscCode.trim().toUpperCase(),
        branchName: schoolFormData.bankDetails.branchName.trim(),
      }
      payload.bankDetails = Object.values(normalizedBankDetails).some(Boolean)
        ? normalizedBankDetails
        : null

      const paypalEmailNorm = schoolFormData.paypalDetails.paypalEmail.trim().toLowerCase()
      payload.paypalDetails = paypalEmailNorm ? { paypalEmail: paypalEmailNorm } : null

      const validStations = (schoolFormData.howToReach || [])
        .filter((s) => s.name.trim() && s.options.some((o) => o.mode))
        .map((s) => ({
          name: s.name.trim(),
          options: s.options.filter((o) => o.mode).map((o) => ({ mode: o.mode, ...(o.details.trim() ? { details: o.details.trim() } : {}) })),
        }))
      payload.howToReach = validStations.length > 0 ? { stations: validStations } : null
      if (schoolCoordinates) {
        payload.coordinates = schoolCoordinates
      }
      const response = await profileService.updateSchoolProfile(payload)
      if (response.success) {
        setSchoolProfile(response.data as any)
        setIsEditingSchool(false)
        setSuccess('School profile updated successfully!')
        toast.success('School profile updated successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update school profile'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSuccess('')

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match')
      return
    }

    const pwdErr = validatePassword(passwordData.newPassword)
    if (pwdErr) {
      setError(pwdErr)
      return
    }

    setSaving(true)

    try {
      const response = await profileService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      )
      if (response.success) {
        setSuccess('Password changed successfully!')
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        })
        setIsChangingPassword(false)
        toast.success('Password changed successfully!')
        setTimeout(() => setSuccess(''), 3000)
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to change password'
      setError(errorMsg)
      toast.error(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-sm text-gray-600 font-medium">Loading profile...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl">
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
        <div className="relative flex items-center justify-between p-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                <UserCircle className="h-6 w-6 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">👤 Profile Management</h1>
              <p className="text-white/90 text-sm">Manage your account and school information</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/media')}
            className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-300 border border-white/30 shadow-lg hover:shadow-xl hover:scale-105"
          >
            <FileText className="h-4 w-4 mr-2" />
            Media
          </button>
        </div>
      </div>

      {/* Tabs with Better Design */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="border-b border-gray-200 bg-gray-50">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('user')}
              className={`flex-1 py-4 px-6 text-center font-semibold text-sm transition-all relative ${
                activeTab === 'user'
                  ? 'text-primary-600 bg-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <User className={`h-5 w-5 inline mr-2 ${activeTab === 'user' ? 'text-primary-600' : ''}`} />
              User Profile
              {activeTab === 'user' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"
                />
              )}
            </button>
            <button
              onClick={() => setActiveTab('school')}
              className={`flex-1 py-4 px-6 text-center font-semibold text-sm transition-all relative ${
                activeTab === 'school'
                  ? 'text-primary-600 bg-white'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              <School className={`h-5 w-5 inline mr-2 ${activeTab === 'school' ? 'text-primary-600' : ''}`} />
              School Profile
              {activeTab === 'school' && (
                <motion.div
                  layoutId="activeTab"
                  className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600"
                />
              )}
            </button>
          </nav>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 text-green-800 px-6 py-4 rounded-lg shadow-md flex items-center"
        >
          <CheckCircle className="h-6 w-6 mr-3 text-primary-600 flex-shrink-0" />
          <span className="font-medium">{success}</span>
        </motion.div>
      )}

      {error && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-red-50 to-rose-50 border-l-4 border-red-500 text-red-800 px-6 py-4 rounded-lg shadow-md flex items-center"
        >
          <XCircle className="h-6 w-6 mr-3 text-red-600 flex-shrink-0" />
          <span className="font-medium">{error}</span>
        </motion.div>
      )}

      {/* User Profile Tab */}
      {activeTab === 'user' && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* Account Status Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-primary-600 rounded-lg">
                  <Shield className="h-6 w-6 text-white" />
                </div>
                {(user as any)?.emailVerified ? (
                  <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    Verified
                  </span>
                ) : (
                  <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    Not Verified
                  </span>
                )}
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Email Status</h3>
              <p className="text-lg font-semibold text-gray-900">
                {(user as any)?.emailVerified ? 'Verified' : 'Pending'}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl p-6 border border-primary-200 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-primary-600 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                {user?.isActive ? (
                  <span className="px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                    Active
                  </span>
                ) : (
                  <span className="px-3 py-1.5 bg-red-100 text-red-700 rounded-full text-sm font-semibold">
                    Inactive
                  </span>
                )}
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">Account Status</h3>
              <p className="text-lg font-semibold text-gray-900">{user?.isActive ? 'Active' : 'Inactive'}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 border border-purple-200 shadow-lg"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="p-3 bg-purple-500 rounded-lg">
                  <Award className="h-6 w-6 text-white" />
                </div>
                <span className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                  {user?.role || 'N/A'}
                </span>
              </div>
              <h3 className="text-sm font-medium text-gray-600 mb-1">User Role</h3>
              <p className="text-lg font-semibold text-gray-900">{user?.role || 'N/A'}</p>
            </motion.div>
          </div>

          {/* Personal Information Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-primary-600 rounded-lg">
                    <User className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">Personal Information</h3>
                </div>
                {!isEditingUser && (
                  <button
                    onClick={() => setIsEditingUser(true)}
                    className="flex items-center px-4 py-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 font-semibold shadow-sm transition-all hover:shadow-md"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {isEditingUser ? (
                <form onSubmit={handleSaveUserProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        First Name
                      </label>
                      <input
                        type="text"
                        name="firstName"
                        value={userFormData.firstName}
                        onChange={handleUserInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Last Name
                      </label>
                      <input
                        type="text"
                        name="lastName"
                        value={userFormData.lastName}
                        onChange={handleUserInputChange}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Mail className="h-4 w-4 mr-2 text-primary-600" />
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={user?.email || ''}
                      disabled
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed"
                    />
                    <p className="mt-2 text-sm text-gray-500 flex items-center">
                      <Sparkles className="h-3 w-3 mr-1" />
                      Email cannot be changed
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                      <Phone className="h-4 w-4 mr-2 text-primary-600" />
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={userFormData.phone}
                      onChange={handleUserInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl transition-all"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-5 w-5 mr-2" />
                          Save Changes
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditingUser(false)
                        setUserFormData({
                          firstName: userProfile?.firstName || '',
                          lastName: userProfile?.lastName || '',
                          phone: userProfile?.phone || '',
                        })
                      }}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      First Name
                    </label>
                    <p className="text-sm font-semibold text-gray-900">{userProfile?.firstName || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-500 mb-1">
                      Last Name
                    </label>
                    <p className="text-sm font-semibold text-gray-900">{userProfile?.lastName || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center">
                      <Mail className="h-3 w-3 mr-1" />
                      Email Address
                    </label>
                    <p className="text-sm font-semibold text-gray-900">{user?.email || 'N/A'}</p>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <label className="block text-sm font-medium text-gray-500 mb-1 flex items-center">
                      <Phone className="h-3 w-3 mr-1" />
                      Phone Number
                    </label>
                    <p className="text-sm font-semibold text-gray-900">{userProfile?.phone || 'Not provided'}</p>
                  </div>
                </div>
              )}
            </div>
          </motion.div>

          {/* Security Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 px-6 py-4 border-b border-amber-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-amber-500 rounded-lg">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="text-base font-semibold text-gray-900">Security Settings</h3>
                </div>
                {!isChangingPassword && (
                  <button
                    onClick={() => setIsChangingPassword(true)}
                    className="flex items-center px-4 py-2 bg-white text-amber-600 rounded-lg hover:bg-amber-50 font-semibold shadow-sm transition-all hover:shadow-md"
                  >
                    Change Password
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {isChangingPassword ? (
                <form onSubmit={handleChangePassword} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Password
                    </label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                        placeholder="Enter your current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showCurrentPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                        placeholder="Enter your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showNewPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                        className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                        placeholder="Confirm your new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                  <div className="flex gap-3 pt-4 border-t border-gray-200">
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex-1 flex items-center justify-center px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl hover:from-amber-600 hover:to-orange-600 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl transition-all"
                    >
                      {saving ? (
                        <>
                          <Loader2 className="h-5 w-5 animate-spin mr-2" />
                          Changing...
                        </>
                      ) : (
                        <>
                          <Lock className="h-5 w-5 mr-2" />
                          Change Password
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsChangingPassword(false)
                        setPasswordData({
                          currentPassword: '',
                          newPassword: '',
                          confirmPassword: '',
                        })
                      }}
                      className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                    <Lock className="h-8 w-8 text-amber-600" />
                  </div>
                  <p className="text-sm text-gray-600 font-medium">Your password is secure</p>
                  <p className="text-sm text-gray-500 mt-1">Click "Change Password" to update it</p>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* School Profile Tab */}
      {activeTab === 'school' && (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6"
    >
          {schoolProfile ? (
            <>
              {/* School Information Card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-primary-600 rounded-lg">
                        <School className="h-5 w-5 text-white" />
                      </div>
                      <h3 className="text-lg font-semibold text-gray-900">School Information</h3>
                    </div>
                    {!isEditingSchool && (
                      <button
                        onClick={() => setIsEditingSchool(true)}
                        className="flex items-center px-4 py-2 bg-white text-primary-600 rounded-lg hover:bg-primary-50 font-semibold shadow-sm transition-all hover:shadow-md"
                      >
                        <Edit className="h-4 w-4 mr-2" />
                        Edit
                      </button>
                    )}
                  </div>
      </div>
      
                <div className="p-6">
                  {isEditingSchool ? (
                    <form onSubmit={handleSaveSchoolProfile} className="space-y-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          School Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={schoolFormData.name}
                          onChange={handleSchoolInputChange}
                          required
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          URL Slug
                        </label>
                        <input
                          type="text"
                          name="slug"
                          value={schoolFormData.slug}
                          onChange={handleSchoolInputChange}
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white font-mono"
                          placeholder="e.g. serenity-yoga-school"
                        />
                        <p className="mt-1 text-sm text-gray-500">Used in your school URL. Use lowercase letters, numbers, and hyphens only.</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Short Description
                        </label>
                        <input
                          type="text"
                          name="shortDescription"
                          value={schoolFormData.shortDescription}
                          onChange={handleSchoolInputChange}
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                          placeholder="Brief description (appears in listings)"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Full Description
                        </label>
                        <textarea
                          name="description"
                          value={schoolFormData.description}
                          onChange={handleSchoolInputChange}
                          rows={5}
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white resize-none"
                          placeholder="Detailed description of your school"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Street
                        </label>
                        <input
                          type="text"
                          name="address"
                          value={schoolFormData.address}
                          onChange={handleSchoolInputChange}
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                          placeholder="e.g. 123 Main Street, Tapovan"
                        />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            City
                          </label>
                          <input
                            type="text"
                            name="city"
                            value={schoolFormData.city}
                            onChange={handleSchoolInputChange}
                            className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                            placeholder="e.g. Rishikesh"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Country
                          </label>
                          <CountrySelect
                            value={schoolFormData.country}
                            onChange={(v) => setSchoolFormData(prev => ({ ...prev, country: v }))}
                            className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                            placeholder="e.g. India"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Area / Locality <span className="text-gray-500 font-normal">(Optional)</span>
                        </label>
                        <input
                          type="text"
                          name="location"
                          value={schoolFormData.location}
                          onChange={handleSchoolInputChange}
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                          placeholder="e.g. Tapovan, Near Ram Jhula"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Map Location <span className="text-gray-500 font-normal">(Optional)</span>
                        </label>
                        {!showMapPicker ? (
                          <div className="space-y-2">
                            <button
                              type="button"
                              onClick={() => setShowMapPicker(true)}
                              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-xl hover:bg-primary-100 transition-colors"
                            >
                              <Map className="h-4 w-4" />
                              {schoolCoordinates ? 'Change location on map' : 'Set location on map'}
                            </button>
                            {schoolCoordinates && (
                              <p className="text-sm text-gray-600">
                                Location set: {schoolCoordinates.lat.toFixed(5)}, {schoolCoordinates.lng.toFixed(5)}
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <MapLocationPicker
                              value={schoolCoordinates}
                              onChange={setSchoolCoordinates}
                              placeholder="Search for your address below or click on the map to set location"
                              initialAddress={[schoolFormData.address, schoolFormData.location, schoolFormData.city, schoolFormData.country].filter(Boolean).join(', ')}
                            />
                            <button
                              type="button"
                              onClick={() => setShowMapPicker(false)}
                              className="text-sm text-gray-500 hover:text-gray-700"
                            >
                              Hide map
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Phone className="h-4 w-4 mr-2 text-primary-600" />
                            Phone
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={schoolFormData.phone}
                            onChange={handleSchoolInputChange}
                            className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                            <Mail className="h-4 w-4 mr-2 text-primary-600" />
                            Email
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={schoolFormData.email}
                            onChange={handleSchoolInputChange}
                            className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                          <Globe className="h-4 w-4 mr-2 text-primary-600" />
                          Website
                        </label>
                        <input
                          type="url"
                          name="website"
                          value={schoolFormData.website}
                          onChange={handleSchoolInputChange}
                          className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-gray-50 focus:bg-white"
                          placeholder="https://example.com"
                        />
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 space-y-5">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary-100 rounded-lg">
                            <Landmark className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">Bank Details</h4>
                            <p className="text-sm text-gray-500">Add your school bank account information for payouts and record keeping.</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Bank Name
                            </label>
                            <input
                              type="text"
                              name="bankName"
                              value={schoolFormData.bankDetails.bankName}
                              onChange={handleSchoolBankDetailsChange}
                              className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                              placeholder="Enter bank name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Account Number
                            </label>
                            <input
                              type="text"
                              name="accountNumber"
                              value={schoolFormData.bankDetails.accountNumber}
                              onChange={handleSchoolBankDetailsChange}
                              className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                              placeholder="Enter account number"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Account Holder Name
                            </label>
                            <input
                              type="text"
                              name="accountHolderName"
                              value={schoolFormData.bankDetails.accountHolderName}
                              onChange={handleSchoolBankDetailsChange}
                              className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                              placeholder="Enter account holder name"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              IFSC Code
                            </label>
                            <input
                              type="text"
                              name="ifscCode"
                              value={schoolFormData.bankDetails.ifscCode}
                              onChange={handleSchoolBankDetailsChange}
                              className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white uppercase"
                              placeholder="Enter IFSC code"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Branch Name
                            </label>
                            <input
                              type="text"
                              name="branchName"
                              value={schoolFormData.bankDetails.branchName}
                              onChange={handleSchoolBankDetailsChange}
                              className="w-full px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                              placeholder="Enter branch name"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="rounded-2xl border border-gray-200 bg-gray-50/70 p-5 space-y-5">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-primary-100 rounded-lg">
                            <Wallet className="h-5 w-5 text-primary-600" />
                          </div>
                          <div>
                            <h4 className="text-base font-semibold text-gray-900">PayPal (international payouts)</h4>
                            <p className="text-sm text-gray-500">
                              Add the PayPal email where you receive payments. Used for international transfers when bank transfer is not used.
                            </p>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            PayPal email
                          </label>
                          <input
                            type="email"
                            name="paypalEmail"
                            value={schoolFormData.paypalDetails.paypalEmail}
                            onChange={handleSchoolPayPalDetailsChange}
                            className="w-full max-w-lg px-4 py-3 text-base border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all bg-white"
                            placeholder="you@example.com"
                            autoComplete="email"
                          />
                        </div>
                      </div>
                      {/* How to Reach */}
                      <HowToReachForm
                        stations={schoolFormData.howToReach?.length ? schoolFormData.howToReach : [{ name: '', options: [{ mode: 'TRAIN', details: '' }] }]}
                        onChange={(stations) => setSchoolFormData((p) => ({ ...p, howToReach: stations }))}
                        inputClass="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
                        schoolName={schoolFormData.name}
                      />
                      <div className="flex gap-3 pt-4 border-t border-gray-200">
                        <button
                          type="submit"
                          disabled={saving}
                          className="flex-1 flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl transition-all"
                        >
                          {saving ? (
                            <>
                              <Loader2 className="h-5 w-5 animate-spin mr-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <Save className="h-5 w-5 mr-2" />
                              Save Changes
                            </>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingSchool(false)
                            setShowMapPicker(false)
                            if (schoolProfile) {
                              const prof = schoolProfile as any
                              const stations = prof.howToReach?.stations
                              setSchoolFormData({
                                name: schoolProfile.name || '',
                                slug: prof.slug || '',
                                description: schoolProfile.description || '',
                                shortDescription: schoolProfile.shortDescription || '',
                                location: schoolProfile.location || '',
                                country: schoolProfile.country || '',
                                city: schoolProfile.city || '',
                                address: schoolProfile.address || '',
                                phone: schoolProfile.phone || '',
                                email: schoolProfile.email || '',
                                website: schoolProfile.website || '',
                                bankDetails: {
                                  bankName: prof.bankDetails?.bankName || '',
                                  accountNumber: prof.bankDetails?.accountNumber || '',
                                  accountHolderName: prof.bankDetails?.accountHolderName || '',
                                  ifscCode: prof.bankDetails?.ifscCode || '',
                                  branchName: prof.bankDetails?.branchName || '',
                                },
                                paypalDetails: {
                                  paypalEmail: prof.paypalDetails?.paypalEmail || '',
                                },
                                howToReach: stations?.length
                                  ? stations.map((s: any) => ({
                                      name: s.name || '',
                                      options: (s.options || []).length
                                        ? s.options.map((o: any) => ({ mode: o.mode || 'TRAIN', details: o.details || '' }))
                                        : [{ mode: 'TRAIN' as const, details: '' }],
                                    }))
                                  : [{ name: '', options: [{ mode: 'TRAIN', details: '' }] }],
                              })
                              setSchoolCoordinates(prof.coordinates?.lat != null && prof.coordinates?.lng != null
                                ? { lat: prof.coordinates.lat, lng: prof.coordinates.lng }
                                : null)
                            }
                          }}
                          className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 font-semibold transition-all"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="p-5 bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-100">
                        <p className="text-sm font-medium text-gray-600 mb-1.5">School Name</p>
                        <p className="text-base font-semibold text-gray-900">{schoolProfile.name || 'N/A'}</p>
                      </div>
                      {schoolProfile.shortDescription && (
                        <div className="p-5 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-600 mb-1.5">Short Description</p>
                          <p className="text-base text-gray-900 leading-relaxed">{schoolProfile.shortDescription}</p>
                        </div>
                      )}
                      {schoolProfile.description && (
                        <div className="md:col-span-2 p-5 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-600 mb-1.5">Description</p>
                          <p className="text-base text-gray-900 whitespace-pre-wrap leading-relaxed">{schoolProfile.description}</p>
                        </div>
                      )}
                      {schoolProfile.address && (
                        <div className="p-5 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-600 mb-1.5">Street</p>
                          <p className="text-base text-gray-900 leading-relaxed">{schoolProfile.address}</p>
                        </div>
                      )}
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-600 mb-1.5">City</p>
                        <p className="text-base font-medium text-gray-900">{schoolProfile.city || 'N/A'}</p>
                      </div>
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-600 mb-1.5">Country</p>
                        <p className="text-base font-medium text-gray-900">{schoolProfile.country || 'N/A'}</p>
                      </div>
                      {schoolProfile.location && (
                        <div className="p-5 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-600 mb-1.5">Area / Locality</p>
                          <p className="text-base text-gray-900 leading-relaxed">{schoolProfile.location}</p>
                        </div>
                      )}
                      {(schoolProfile as any).coordinates && (
                        <div className="md:col-span-2 p-5 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-600 mb-2">Map Location</p>
                          {!showSchoolMap ? (
                            <button
                              type="button"
                              onClick={() => setShowSchoolMap(true)}
                              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-primary-600 bg-primary-50 border border-primary-200 rounded-xl hover:bg-primary-100 transition-colors"
                            >
                              <Map className="h-4 w-4" />
                              Show map
                            </button>
                          ) : (
                            <div>
                              <MapDisplay
                                coordinates={(schoolProfile as any).coordinates}
                                onClose={() => setShowSchoolMap(false)}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-600 mb-1.5 flex items-center">
                          <Phone className="h-4 w-4 mr-1.5 text-primary-600" />
                          Phone
                        </p>
                        <p className="text-base font-medium text-gray-900">{schoolProfile.phone || 'N/A'}</p>
                      </div>
                      <div className="p-5 bg-gray-50 rounded-xl">
                        <p className="text-sm font-medium text-gray-600 mb-1.5 flex items-center">
                          <Mail className="h-4 w-4 mr-1.5 text-primary-600" />
                          Email
                        </p>
                        <p className="text-base font-medium text-gray-900">{schoolProfile.email || 'N/A'}</p>
                      </div>
                      {schoolProfile.website && (
                        <div className="md:col-span-2 p-5 bg-gray-50 rounded-xl">
                          <p className="text-sm font-medium text-gray-600 mb-1.5 flex items-center">
                            <Globe className="h-4 w-4 mr-1.5 text-primary-600" />
                            Website
                          </p>
                          <a
                            href={schoolProfile.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-base text-primary-600 hover:text-primary-700 font-medium transition-colors break-all"
                          >
                            {schoolProfile.website}
                          </a>
                        </div>
                      )}
                      <div className="md:col-span-2 p-5 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-4">
                          <Landmark className="h-4 w-4 text-primary-600" />
                          <p className="text-sm font-medium text-gray-600">Bank Details</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <p className="text-sm font-medium text-gray-500 mb-1.5">Bank Name</p>
                            <p className="text-base font-medium text-gray-900">{schoolProfile.bankDetails?.bankName || 'Not provided'}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <p className="text-sm font-medium text-gray-500 mb-1.5">Account Number</p>
                            <p className="text-base font-medium text-gray-900">{schoolProfile.bankDetails?.accountNumber || 'Not provided'}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <p className="text-sm font-medium text-gray-500 mb-1.5">Account Holder Name</p>
                            <p className="text-base font-medium text-gray-900">{schoolProfile.bankDetails?.accountHolderName || 'Not provided'}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <p className="text-sm font-medium text-gray-500 mb-1.5">IFSC Code</p>
                            <p className="text-base font-medium text-gray-900">{schoolProfile.bankDetails?.ifscCode || 'Not provided'}</p>
                          </div>
                          <div className="bg-white rounded-xl p-4 border border-gray-100">
                            <p className="text-sm font-medium text-gray-500 mb-1.5">Branch Name</p>
                            <p className="text-base font-medium text-gray-900">{schoolProfile.bankDetails?.branchName || 'Not provided'}</p>
                          </div>
                        </div>
                      </div>
                      <div className="md:col-span-2 p-5 bg-gray-50 rounded-xl">
                        <div className="flex items-center gap-2 mb-4">
                          <Wallet className="h-4 w-4 text-primary-600" />
                          <p className="text-sm font-medium text-gray-600">PayPal (international payouts)</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 border border-gray-100 max-w-lg">
                          <p className="text-sm font-medium text-gray-500 mb-1.5">PayPal email</p>
                          <p className="text-base font-medium text-gray-900 break-all">
                            {schoolProfile.paypalDetails?.paypalEmail || 'Not provided'}
                          </p>
                        </div>
                      </div>
                      {(schoolProfile as any).howToReach?.stations?.length > 0 && (
                        <div className="md:col-span-2 p-5 bg-gray-50 rounded-xl space-y-3">
                          <p className="text-sm font-medium text-gray-600 mb-2">How to Reach</p>
                          {(schoolProfile as any).howToReach.stations.map((st: any, i: number) => (
                            <div key={i} className="border-l-2 border-primary-200 pl-3">
                              <p className="font-medium text-gray-900">{st.name}</p>
                              <div className="mt-1 flex flex-wrap gap-2">
                                {(st.options || []).map((o: any, j: number) => (
                                  <span
                                    key={j}
                                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800"
                                  >
                                    {o.mode === 'TRAIN' && 'By Train'}
                                    {o.mode === 'BUS' && 'By Bus'}
                                    {o.mode === 'TAXI' && 'By Taxi'}
                                    {o.mode === 'AIR' && 'By Air'}
                                    {o.details ? ` – ${o.details}` : ''}
                                  </span>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Verification Documents Section */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-amber-50 to-amber-100 px-6 py-4 border-b border-amber-200">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                      <Shield className="h-5 w-5 mr-2 text-amber-600" />
                      Verification Documents
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate('/profile/school-documents')}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-amber-800 bg-white border border-amber-200 rounded-lg hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-colors"
                      >
                        <Upload className="h-4 w-4 mr-1.5" />
                        Update documents
                      </button>
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold ${
                      verificationStatus === 'VERIFIED'
                        ? 'bg-green-100 text-green-700'
                        : verificationStatus === 'REJECTED'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {verificationStatus === 'VERIFIED' && <CheckCircle className="h-3.5 w-3.5 mr-1" />}
                      {verificationStatus === 'REJECTED' && <XCircle className="h-3.5 w-3.5 mr-1" />}
                      {verificationStatus === 'PENDING' && <Clock className="h-3.5 w-3.5 mr-1" />}
                      {verificationStatus === 'VERIFIED' ? 'Verified' : verificationStatus}
                    </span>
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  {loadingVerification ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                      <span className="ml-2 text-base text-gray-500">Loading documents...</span>
                    </div>
                  ) : (verificationDocuments.length > 0 || Object.keys(verificationMeta).length > 0) ? (
                    <div className="space-y-5">
                      {/* Document Numbers / Details */}
                      {Object.keys(verificationMeta).length > 0 && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {verificationMeta.businessRegNumber && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Business Reg. No.</p>
                              <p className="text-base font-medium text-gray-900 mt-1.5">{verificationMeta.businessRegNumber}</p>
                            </div>
                          )}
                          {verificationMeta.gstNumber && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">GST Number</p>
                              <p className="text-base font-medium text-gray-900 mt-1.5">{verificationMeta.gstNumber}</p>
                            </div>
                          )}
                          {verificationMeta.yogaAllianceNumber && (
                            <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                              <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Yoga Alliance Cert.</p>
                              <p className="text-base font-medium text-gray-900 mt-1.5">{verificationMeta.yogaAllianceNumber}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Uploaded Files */}
                      {verificationDocuments.length > 0 && (
                        <>
                          <p className="text-sm text-gray-500 mb-3">
                            {verificationDocuments.length} file{verificationDocuments.length !== 1 ? 's' : ''} uploaded
                          </p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {verificationDocuments.map((doc, index) => {
                              const url = typeof doc === 'string' ? doc : doc.url
                              const docType = typeof doc === 'string' ? 'additional' : (doc.type || 'additional')
                              const isPdf = url.split('?')[0].toLowerCase().endsWith('.pdf')
                              const labels: Record<string, string> = {
                                businessReg: 'Business Registration',
                                gst: 'GST Certificate',
                                addressProof: 'Address Proof',
                                yogaAlliance: 'Yoga Alliance Certificate',
                                additional: 'Additional Document',
                              }
                              const label = labels[docType] || docType
                              return (
                                <a
                                  key={index}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="group flex items-center p-4 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all"
                                >
                                  <div className={`p-2.5 rounded-lg mr-3 ${isPdf ? 'bg-red-100' : 'bg-primary-100'}`}>
                                    <FileText className={`h-5 w-5 ${isPdf ? 'text-red-600' : 'text-primary-600'}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-base font-medium text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                                      {label}
                                    </p>
                                    <p className="text-sm text-gray-500 truncate">{isPdf ? 'PDF' : 'Image'}</p>
                                  </div>
                                  <ExternalLink className="h-4 w-4 text-gray-400 group-hover:text-primary-600 flex-shrink-0" />
                                </a>
                              )
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-full mb-4">
                        <AlertCircle className="h-7 w-7 text-gray-400" />
                      </div>
                      <p className="text-base font-medium text-gray-900 mb-1.5">No Documents Uploaded</p>
                      <p className="text-sm text-gray-500">Verification documents were not submitted during school creation.</p>
                    </div>
                  )}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-primary-50 to-primary-100 px-6 py-4 border-b border-primary-200">
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                    <Sparkles className="h-5 w-5 mr-2 text-primary-600" />
                    Quick Actions
                  </h3>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <button
                      onClick={() => navigate('/media')}
                      className="group flex items-center p-5 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left"
                    >
                      <div className="p-3 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors mr-4">
                        <FileText className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                          Manage Media
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Upload logos, images, and videos</p>
                      </div>
                    </button>
                    <button
                      onClick={() => navigate('/courses')}
                      className="group flex items-center p-5 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left"
                    >
                      <div className="p-3 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors mr-4">
                        <BookOpen className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                          Manage Courses
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Create and edit courses</p>
                      </div>
                    </button>
                    <button
                      onClick={() => navigate('/bookings')}
                      className="group flex items-center p-5 border-2 border-gray-200 rounded-xl hover:border-primary-500 hover:bg-primary-50 transition-all text-left"
                    >
                      <div className="p-3 bg-primary-100 rounded-lg group-hover:bg-primary-200 transition-colors mr-4">
                        <Calendar className="h-6 w-6 text-primary-600" />
                      </div>
                      <div>
                        <p className="text-base font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                          View Bookings
                        </p>
                        <p className="text-sm text-gray-600 mt-1">Manage student bookings</p>
                      </div>
                    </button>
                  </div>
      </div>
    </motion.div>
            </>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white shadow-lg rounded-xl p-12 text-center border border-gray-100"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mb-6">
                <School className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No School Profile Found</h3>
              <p className="text-base text-gray-600 mb-6">Please contact support to set up your school profile.</p>
            </motion.div>
          )}
        </motion.div>
      )}
    </div>
  )
}

export default Profile
